// src/components/AppearanceSwitch.jsx
// Light · Auto · Dark (3.45.0): one switch, used in Preferences, the Theme
// section of your menu (3.47.0, `stretch`), the header (3.47.0, `size="sm"`;
// `compact` = icons only, on phones). Wherever it's changed, the choice saves
// to your account straight away (hooks/usePreferences) and every copy shows
// it; theme/useAppearance applies it. Until someone picks, it shows their
// client's starting mode, else Auto. A highlight slides to the chosen option.
import { usePreferences } from '../hooks/usePreferences';
import { useAuth } from '../hooks/useAuth';
import { APPEARANCES } from '../theme/useAppearance';
import { effectiveMode } from '../theme/themes';
import Emo from './Emo';

export default function AppearanceSwitch({ compact = false, size, stretch = false, id = 'appearance' }) {
  const { userInfo } = useAuth();
  const { prefs, setPreference } = usePreferences();
  const value = effectiveMode(prefs.appearance, userInfo?.clientMode);
  const index = APPEARANCES.findIndex(a => a.id === value);

  function onKey(e) {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = APPEARANCES[(index + step + APPEARANCES.length) % APPEARANCES.length];
    setPreference('appearance', next.id);
    document.getElementById(`${id}-${next.id}`)?.focus();
  }

  return (
    <div role="radiogroup" aria-label="Light or dark" className={'cp-appear' + (compact ? ' compact' : '') + (size === 'sm' ? ' sm' : '') + (stretch ? ' stretch' : '')} onKeyDown={onKey}
      style={{ '--n': APPEARANCES.length, '--i': index }}>
      <style>{CSS}</style>
      <span className="cp-appear-ind" aria-hidden="true" />
      {APPEARANCES.map(a => (
        <button key={a.id} id={`${id}-${a.id}`} type="button" role="radio" aria-checked={value === a.id} tabIndex={value === a.id ? 0 : -1}
          title={compact ? undefined : a.hint} aria-label={compact ? `${a.label}: ${a.hint}` : undefined}
          className={value === a.id ? 'on' : ''} onClick={() => setPreference('appearance', a.id)}>
          <span className="ico" aria-hidden="true"><Emo e={a.icon} /></span>
          {!compact && <span>{a.label}</span>}
          {/* 3.47.2: compact shows the mode's name in a small tip on hover */}
          {compact && <span className="tip" aria-hidden="true">{a.id === 'auto' ? 'Auto (same as your computer)' : a.label}</span>}
        </button>
      ))}
    </div>
  );
}

const CSS = `
.cp-appear { position: relative; display: inline-grid; grid-template-columns: repeat(var(--n), 1fr); padding: 3px; gap: 0;
  border-radius: 999px; background: var(--c-eceff1); border: 1.5px solid var(--c-cfd8dc); isolation: isolate; }
.cp-appear button { position: relative; z-index: 1; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  min-width: 84px; padding: 6px 14px; border: none; background: none; border-radius: 999px; cursor: pointer;
  font: inherit; font-size: 13px; font-weight: 700; color: var(--t-546e7a); transition: color .2s ease; }
.cp-appear.compact button { min-width: 0; width: 30px; height: 26px; padding: 0; font-size: 14px; }
.cp-appear button .tip { position: absolute; top: calc(100% + 7px); left: 50%; transform: translateX(-50%) translateY(-4px); opacity: 0; pointer-events: none;
  white-space: nowrap; font-size: 11.5px; font-weight: 700; padding: 3px 8px; border-radius: 6px; z-index: 5;
  background: var(--c-263238); color: var(--t-ffffff); box-shadow: 0 4px 12px rgb(var(--c-000000-rgb) / 0.2); transition: opacity .15s ease, transform .15s ease; }
.cp-appear button:hover .tip, .cp-appear button:focus-visible .tip { opacity: 1; transform: translateX(-50%); }
.cp-appear.sm button { min-width: 0; padding: 4px 10px; font-size: 12px; gap: 4px; }
.cp-appear.stretch { display: grid; width: 100%; }
.cp-appear.stretch button { min-width: 0; padding: 4px 6px; font-size: 12.5px; gap: 5px; }
.cp-appear button.on { color: var(--t-ffffff); }
.cp-appear button:hover:not(.on) { color: var(--t-00695c); }
.cp-appear button:focus-visible { outline: 2px solid var(--c-00897b); outline-offset: 2px; }
.cp-appear button .ico { display: inline-block; transition: transform .35s cubic-bezier(.3,1.6,.5,1); }
.cp-appear button.on .ico { transform: rotate(-12deg) scale(1.12); }
.cp-appear button:hover .ico { transform: rotate(14deg) scale(1.15); }
.cp-appear-ind { position: absolute; z-index: 0; top: 3px; bottom: 3px; left: 3px; width: calc((100% - 6px) / var(--n));
  transform: translateX(calc(var(--i) * 100%)); border-radius: 999px;
  background: linear-gradient(135deg, var(--c-00695c), var(--c-00796b) 55%, var(--b-2e7d32));
  box-shadow: 0 3px 10px rgb(var(--c-00897b-rgb) / 0.35), inset 0 1px 0 rgb(var(--c-ffffff-rgb) / 0.25);
  transition: transform .38s cubic-bezier(.34,1.4,.64,1); overflow: hidden; }
.cp-appear-ind::after { content: ''; position: absolute; top: 0; bottom: 0; width: 45%; left: -60%;
  background: linear-gradient(100deg, transparent, rgb(var(--c-ffffff-rgb) / 0.35), transparent); transform: skewX(-18deg);
  animation: cpAppearShine 5s ease-in-out 1s infinite; }
@keyframes cpAppearShine { 0% { left: -60%; } 40%, 100% { left: 130%; } }
`;
